import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Flag, ImagePlus, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { supportApi } from '@/api/support.api';
import { readApiErrorMessage } from '@/lib/api';
import {
  BUG_REPORT_SCREENSHOT_MAX_BYTES,
  beginBugReportCaptureHide,
  captureBugReportScreenshot,
  captureBugReportScreenshotViaDisplayMedia,
  readClipboardImageFile,
  waitForBugReportCapturePaint,
} from '@/lib/bug-report-screenshot';

const MAX_SCREENSHOT_BYTES = BUG_REPORT_SCREENSHOT_MAX_BYTES;

export function BugReportFooterButton({ className }: { className?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn('lo-bug-report-btn', className)}
        aria-haspopup="dialog"
        data-hide-for-bug-report-capture
      >
        <Flag className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
        <span>{t('bugReport.footerButton', 'Signaler un bug')}</span>
      </button>
      <BugReportModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

type BugReportModalProps = {
  open: boolean;
  onClose: () => void;
};

export function BugReportModal({ open, onClose }: BugReportModalProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const user = useAuthStore((s) => s.user);

  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail(user?.email?.trim() ?? '');
    setDescription('');
    setScreenshotFile(null);
    setScreenshotPreview(null);
    setCapturingScreenshot(false);
  }, [open, user?.email]);

  useEffect(() => {
    if (!screenshotFile) {
      setScreenshotPreview(null);
      return;
    }
    const url = URL.createObjectURL(screenshotFile);
    setScreenshotPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshotFile]);

  const applyScreenshotFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/')) {
        toast.error(t('bugReport.invalidScreenshot', 'Veuillez choisir une image.'));
        return;
      }
      if (file.size > MAX_SCREENSHOT_BYTES) {
        toast.error(t('bugReport.screenshotTooLarge', 'Image trop volumineuse (max 5 Mo).'));
        return;
      }
      setScreenshotFile(file);
    },
    [t],
  );

  useEffect(() => {
    if (!open) return;
    const onPaste = (event: ClipboardEvent) => {
      const file = readClipboardImageFile(event);
      if (!file) return;
      event.preventDefault();
      applyScreenshotFile(file);
      toast.success(t('bugReport.screenshotCaptured', 'Capture ajoutée.'));
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [applyScreenshotFile, open, t]);

  const onPickScreenshot = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    applyScreenshotFile(file);
  };

  const captureScreenshot = useCallback(async () => {
    setCapturingScreenshot(true);
    const hideSession = beginBugReportCaptureHide();
    try {
      await waitForBugReportCapturePaint();
      try {
        const file = await captureBugReportScreenshotViaDisplayMedia();
        applyScreenshotFile(file);
        toast.success(t('bugReport.screenshotCaptured', 'Capture ajoutée.'));
      } catch (displayMediaError) {
        const cancelled =
          displayMediaError instanceof DOMException &&
          (displayMediaError.name === 'NotAllowedError' || displayMediaError.name === 'AbortError');
        if (cancelled) {
          toast.info(t('bugReport.screenshotCaptureCancelled', 'Capture annulée.'));
          return;
        }
        if (import.meta.env.DEV) {
          console.warn('[bug-report] display media failed, trying html2canvas', displayMediaError);
        }
        try {
          const file = await captureBugReportScreenshot();
          applyScreenshotFile(file);
          toast.success(t('bugReport.screenshotCaptured', 'Capture ajoutée.'));
        } catch (err) {
          if (import.meta.env.DEV) {
            console.warn('[bug-report] capture failed', err);
          }
          toast.error(
            t(
              'bugReport.screenshotCaptureError',
              'Impossible de capturer l’écran. Collez une capture (Ctrl+V / Cmd+V) ou importez une image.',
            ),
          );
        }
      }
    } finally {
      hideSession.restore();
      setCapturingScreenshot(false);
    }
  }, [applyScreenshotFile, t]);

  const clearScreenshot = () => {
    setScreenshotFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = useCallback(async () => {
    const trimmedEmail = email.trim();
    const trimmedDescription = description.trim();
    if (!trimmedEmail || !trimmedDescription) {
      toast.error(t('bugReport.requiredFields', 'Email et description sont requis.'));
      return;
    }

    setSending(true);
    try {
      let screenshotUrl: string | undefined;
      if (screenshotFile) {
        const uploadRes = await supportApi.uploadBugReportScreenshot(screenshotFile);
        const url = (uploadRes as { data?: { url?: string } })?.data?.url;
        if (typeof url === 'string' && url.trim()) {
          screenshotUrl = url.trim();
        }
      }

      await supportApi.sendBugReport({
        email: trimmedEmail,
        description: trimmedDescription,
        screenshotUrl,
        pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      });

      toast.success(t('bugReport.sent', 'Merci, votre signalement a été envoyé.'));
      onClose();
    } catch (err) {
      toast.error(readApiErrorMessage(err, t('bugReport.sendError', 'Envoi impossible. Réessayez.')));
    } finally {
      setSending(false);
    }
  }, [description, email, onClose, screenshotFile, t]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      excludeFromBugReportCapture
        size="sm"
        showCloseButton={false}
        className="!max-w-md !rounded-3xl border border-gray-100 shadow-2xl dark:border-gray-700"
        ariaLabelledBy={titleId}
      >
        <div className="px-6 pb-6 pt-8">
          <h2 id={titleId} className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('bugReport.title', 'Signaler un problème')}
          </h2>

          <div className="mt-6 space-y-4">
            <label className="block text-sm">
              <span className="mb-1.5 block text-gray-700 dark:text-gray-300">
                {t('bugReport.email', 'Email')}
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none transition focus:border-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </label>

            <label className="block text-sm">
              <span className="mb-1.5 block text-gray-700 dark:text-gray-300">
                {t(
                  'bugReport.descriptionLabel',
                  'Description (un screenshot aide énormément 📸) (required)',
                )}
              </span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                required
                placeholder={t('bugReport.descriptionPlaceholder', 'Décrivez le problème rencontré...')}
                className="w-full resize-y rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none transition focus:border-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </label>

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="sr-only"
              tabIndex={-1}
              onChange={onPickScreenshot}
            />

            {screenshotPreview ? (
              <div className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-600">
                <img
                  src={screenshotPreview}
                  alt={t('bugReport.screenshotPreview', 'Aperçu de la capture')}
                  className="max-h-40 w-full object-contain bg-gray-50 dark:bg-gray-900"
                />
                <button
                  type="button"
                  onClick={clearScreenshot}
                  className="absolute end-2 top-2 rounded-full bg-black/70 p-1 text-white hover:bg-black"
                  aria-label={t('bugReport.removeScreenshot', 'Retirer la capture')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => void captureScreenshot()}
                  disabled={capturingScreenshot || sending}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-900 transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
                >
                  {capturingScreenshot ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <ImagePlus className="h-4 w-4" aria-hidden />
                  )}
                  {capturingScreenshot
                    ? t('bugReport.capturingScreenshot', 'Capture de l’écran…')
                    : t('bugReport.addScreenshot', 'Capturer l’écran')}
                </button>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={capturingScreenshot || sending}
                  className="w-full text-center text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
                >
                  {t(
                    'bugReport.importScreenshot',
                    'Ou importer / coller une image (Ctrl+V)',
                  )}
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={sending || capturingScreenshot}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-black px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-900 disabled:opacity-60 dark:bg-white dark:text-black dark:hover:bg-gray-100"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {t('bugReport.send', 'Envoyer')}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={sending || capturingScreenshot}
              className="flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-900 transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              {t('common.cancel', 'Annuler')}
            </button>
          </div>
        </div>
      </Modal>
  );
}
